import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StudentClassesService from '../../services/StudentClassesService';
import { presentationLocale } from '../../i18n/calendarLabels';
import { buildSchoolYearLabel, normalizeSchoolYearStartYear } from '../common/schoolYear';
import { buildStudentClassRoute } from './studentClassesNavigation';
import { useTeacherAccess } from './TeacherAccessProvider';
import './studentClasses.scss';

const empty = () => ({ items: [], loaded: false, loading: false, error: false, stale: false, nextCursor: null, failedCursor: null, visibility: null });
const buttonClass = 'btn manage-button manage-teal-button';

function StudentClassesView({ studentId, schoolYearStart, originSearch }) {
  const { t, i18n } = useTranslation();
  const tr = (key, values) => t(`manage:studentClasses.${key}`, values);
  const displayDate = value => (value ? new Intl.DateTimeFormat(presentationLocale(i18n.language), {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`)) : '—');
  const [expanded, setExpanded] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [current, setCurrent] = useState(empty);
  const [history, setHistory] = useState(empty);
  const requests = useRef({ current: null, history: null });
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
    Object.values(requests.current).forEach(request => { if (request) request.abort(); });
  }, []);

  const load = async (section, cursor = null) => {
    if (!studentId || studentId === 'new' || requests.current[section]) return;
    const controller = new AbortController();
    requests.current[section] = controller;
    const update = section === 'current' ? setCurrent : setHistory;
    update(previous => ({ ...previous, loading: true, error: false, failedCursor: cursor }));
    try {
      const response = await StudentClassesService.getOverview(studentId, { schoolYearStart, section, cursor, signal: controller.signal });
      if (!mounted.current || requests.current[section] !== controller) return;
      update(previous => ({ ...previous,
        loading: false,
        loaded: true,
        error: false,
        stale: false,
        nextCursor: response.nextCursor,
        visibility: response.visibility,
        items: cursor
          ? [...new Map([...previous.items, ...response.items].map(item => [item.key, item])).values()] : response.items }));
    } catch (error) {
      if (!mounted.current || requests.current[section] !== controller || error.name === 'AbortError') return;
      update(previous => ({ ...previous, loading: false, error: error.code || true, stale: previous.loaded && !cursor }));
    } finally {
      if (requests.current[section] === controller) requests.current[section] = null;
    }
  };
  const toggleCurrent = () => {
    setExpanded(!expanded);
    if (!expanded && !current.loaded && !current.error) load('current');
  };
  const toggleHistory = () => {
    setHistoryExpanded(!historyExpanded);
    if (!historyExpanded && !history.loaded && !history.error) load('history');
  };
  const refresh = () => {
    if (requests.current.current) return;
    if (requests.current.history) requests.current.history.abort();
    requests.current.history = null;
    setHistory(empty());
    setHistoryExpanded(false);
    load('current');
  };
  const showRows = items => (
    <table className="student-classes-table">
      <thead><tr>{['class', 'schedule', 'teacher', 'status'].map(key => <th className="manage-table-title" key={key} scope="col">{tr(key)}</th>)}</tr></thead>
      <tbody>{items.map((row) => {
        const route = buildStudentClassRoute(row, studentId, schoolYearStart, originSearch);
        const unsupportedYear = row.canOpenDetails && normalizeSchoolYearStartYear(row.schoolYearStart) !== row.schoolYearStart;
        const period = row.classFromDate || row.classTillDate ? `${displayDate(row.classFromDate)} – ${displayDate(row.classTillDate)}` : tr('unknownDates');
        return (
          <tr key={row.key}>
            <td data-label={tr('class')}>
              {route ? <Link to={route}>{row.className || tr('unknownClass')}</Link> : <strong>{row.className || tr('unknownClass')}</strong>}
              {row.durationMinutes && <small>{tr('duration', { count: row.durationMinutes })}</small>}
              {unsupportedYear && <small>{tr('unsupportedYear')}</small>}
              <small>{tr('classPeriod', { period })}</small>
            </td>
            <td data-label={tr('schedule')}>{row.dayOfWeek ? tr(`days.${row.dayOfWeek}`) : '—'}<small>{row.startTime || '—'}{row.endTime ? ` – ${row.endTime}` : ''}</small></td>
            <td data-label={tr('teacher')}>{row.teacherName || '—'}</td>
            <td data-label={tr('status')}><span className={`student-classes-status student-classes-status-${row.status}`}>{tr(`statuses.${row.status}`)}</span>
              {row.status === 'upcoming' && <small>{tr('startsOn', { date: displayDate(row.classFromDate) })}</small>}
            </td>
          </tr>
        );
      })}
      </tbody>
    </table>
  );
  const showState = (state, section) => (
    <>
      {state.visibility && (state.visibility.scope === 'restricted' || !state.visibility.sources.memberships || !state.visibility.sources.register) &&
        <p className="alert alert-info">{tr('restricted')}</p>}
      {state.stale && <p role="status" className="alert alert-warning">{tr('stale')}</p>}
      {state.error && (
        <div role="alert" className="alert alert-danger">{tr(({ 401: 'signedOut', 403: 'forbidden', 404: 'notFound', 400: 'invalidPage' })[state.error] || 'error')}
          <button type="button" className={buttonClass} onClick={() => load(section, state.failedCursor)}>{tr('retry')}</button>
        </div>
      )}
      {state.loading && <p role="status">{tr('loading')}</p>}
      {state.loaded && <p className="student-classes-count">{tr('loadedCount', { count: state.items.length })}{state.nextCursor ? ` · ${tr('moreAvailable')}` : ''}</p>}
      {state.loaded && !state.items.length && <p>{tr(section === 'current' ? 'emptyCurrent' : 'emptyHistory')}</p>}
      {section === 'current' && state.items.length > 0 && showRows(state.items)}
      {section === 'history' && [...new Set(state.items.map(row => row.schoolYearStart))].map(year => (
        <div key={year}><h4>{buildSchoolYearLabel(year)}</h4>{showRows(state.items.filter(row => row.schoolYearStart === year))}</div>
      ))}
      {state.nextCursor && !state.error && <button type="button" className={buttonClass} disabled={state.loading} onClick={() => load(section, state.nextCursor)}>{tr('loadMore')}</button>}
    </>
  );
  const contentId = `student-classes-${encodeURIComponent(studentId || 'new')}`;
  return (
    <section className="student-classes" aria-label={tr('title')}>
      {!studentId || studentId === 'new' ? <p>{tr('unsaved')}</p> : (
        <>
          <button type="button" className={buttonClass} aria-expanded={expanded} aria-controls={contentId} disabled={current.loading && !current.loaded} onClick={toggleCurrent}>
            {tr(expanded ? 'hide' : 'show')}
          </button>
          {expanded && (
          <div id={contentId}>
            <div className="student-classes-heading"><h3>{tr('title')} · {buildSchoolYearLabel(schoolYearStart)}</h3>
              <button type="button" className={buttonClass} disabled={current.loading} onClick={refresh}>{tr('refresh')}</button>
            </div>
            {showState(current, 'current')}
            <div className="student-classes-history">
              <button type="button" className={buttonClass} aria-expanded={historyExpanded} aria-controls={`${contentId}-history`} onClick={toggleHistory}>
                {tr(historyExpanded ? 'hideHistory' : 'showHistory')}
              </button>
              {historyExpanded && <div id={`${contentId}-history`}><p className="mt-3">{tr('historyEvidence')}</p>{showState(history, 'history')}</div>}
            </div>
          </div>
          )}
        </>
      )}
    </section>
  );
}

StudentClassesView.propTypes = { studentId: PropTypes.string, schoolYearStart: PropTypes.number.isRequired, originSearch: PropTypes.string };
StudentClassesView.defaultProps = { studentId: null, originSearch: '' };

export default function StudentClassesSection({ studentId, schoolYearStart, contextKey, originSearch }) {
  const access = useTeacherAccess();
  // Remounting clears local data and aborts requests without initiating any read.
  const key = JSON.stringify([studentId, schoolYearStart, contextKey, access.contextKey, access.permissionsVersion, access.status, access.extensions]);
  return <StudentClassesView key={key} studentId={studentId} schoolYearStart={schoolYearStart} originSearch={originSearch} />;
}
StudentClassesSection.propTypes = { ...StudentClassesView.propTypes, contextKey: PropTypes.string.isRequired };
StudentClassesSection.defaultProps = StudentClassesView.defaultProps;
